import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CommentIcon from '@mui/icons-material/Comment';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoIcon from '@mui/icons-material/Info';
import { CheckCircle, ExternalLink, FolderOpen, Key, Lock, Rocket, Settings, Zap } from 'lucide-react';

const StartReviewingStep = () => {
  return (
    <>
      <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: '2.25rem', lineHeight: '2.5rem' }}>Step 4: Start Reviewing</Typography>
      <Typography variant="body1" color="#6b7280" gutterBottom sx={{ mb: 4, fontSize: '1.25rem', lineHeight: '1.75rem' }}>
        Once your project is configured and webhooks are set up, you're ready to start receiving AI-powered code reviews!
      </Typography>

      {/* Automatic Analysis & What the AI Reviews Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Automatic Analysis Card */}
        <Grid item xs={12} md={6}>
          <Box sx={{
            bgcolor: '#fff',
            border: '0 solid #e2e8f0',
            height: '100%',
            p: 6,
            borderRadius: '12px'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Box sx={{
                width: { xs: 22, sm: 30 },
                height: { xs: 22, sm: 30 },
                borderRadius: '50%',
                bgcolor: '#dbeafe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Rocket color='#3b82f6' size={18} />
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                Automatic Analysis
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {[
                "Create or update a pull request in your repository",
                "Our webhook receives the notification automatically",
                "AI analyzes your code changes and provides feedback",
                "Review comments are posted directly to your PR"
              ].map((step, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: '#3b82f6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    flexShrink: 0,
                    mt: 0.5
                  }}>
                    {index + 1}
                  </Box>
                  <Typography variant="body1" color="#1f1f1f" 
                  sx={{ 
                    lineHeight: '1.25rem',
                    fontSize: '0.875rem',
                  }}>
                    {step}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>

        {/* What the AI Reviews Card */}
        <Grid item xs={12} md={6}>
          <Box sx={{
            bgcolor: '#fff',
            border: '0 solid #e2e8f0',
            height: '100%',
            p: 6,
            borderRadius: '12px'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Box sx={{
                width: { xs: 22, sm: 30 },
                height: { xs: 22, sm: 30 },
                borderRadius: '50%',
                bgcolor: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CheckCircle color='#21c45d' size={18} />
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                What the AI Reviews
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                "Code quality and best practices",
                "Security vulnerabilities and potential issues",
                "Performance optimizations",
                "Code style and consistency",
                "Documentation and readability"
              ].map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircle color='#21c45d' size={18} />
                  <Typography variant="body1" color="#1f1f1f" 
                  sx={{ 
                    lineHeight: '1.25rem',
                    fontSize: '0.875rem',
                  }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Review Feedback Types Section */}
      <Box sx={{ mb: 4, bgcolor: '#fff', border: '0 solid #e2e8f0', p: 6, borderRadius: '12px' }}>
        <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f', mb: 1 }}>
          Review Feedback Types
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose how AI feedback is delivered to your pull requests
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Inline Comments Card */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 3,
              border: '0 solid #e2e8f0',
              p: 6,
              borderRadius: '12px'
            }}>
              <Box sx={{
                width: { xs: 22, sm: 30 },
                height: { xs: 22, sm: 30 },
                borderRadius: '50%',
                bgcolor: '#dbeafe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <ExternalLink color='#3b82f6' size={16} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start', flexDirection: 'column', justifyContent: 'flex-start', gap: 1, mb: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                  Inline Comments
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Detailed feedback on specific lines of code
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* PR Summary Card */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 3,
              border: '0 solid #e2e8f0',
              p: 6,
              borderRadius: '12px'
            }}>
              <Box sx={{
                width: { xs: 22, sm: 30 },
                height: { xs: 22, sm: 30 },
                borderRadius: '50%',
                bgcolor: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Zap color='#21c45d' size={16} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start', flexDirection: 'column', justifyContent: 'flex-start', gap: 1, mb: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                  PR Summary
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  High-level overview of the entire pull request
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Configuration Info Box */}
        <Alert severity="info" sx={{ bgcolor: '#ebf2fe', color: '#000', border: 'none', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              Configuration
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            You can configure this in your project settings under 'Review Comment Mode'
          </Typography>
        </Alert>
      </Box>

      {/* Dashboard Features Section */}
      <Box sx={{ mb: 4, bgcolor: '#fff', border: '0 solid #e2e8f0', p: 6, borderRadius: '12px' }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f', mb: 1 }}>
            Dashboard Features
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage your projects, team, and subscription from one place
          </Typography>

          <Grid container spacing={3}>
            {/* Project Management */}
            <Grid item xs={12} md={4}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2, mb: 2 }}>
                  <Box sx={{
                    width: { xs: 22, sm: 30 },
                    height: { xs: 22, sm: 30 },
                    borderRadius: '50%',
                    bgcolor: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <FolderOpen color='#3b82f6' size={16} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                    Project Management
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, textAlign: 'left',ml: 9 }}>
                  {[
                    "View all projects and their status",
                    "Monitor PR analysis history",
                    "Manage project configurations"
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        bgcolor: '#6b7280',
                        flexShrink: 0,
                        mt: 1.5
                      }} />
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Team Management */}
            <Grid item xs={12} md={4}>
              <Box >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2, mb: 2 }}>
                  <Box sx={{
                    width: { xs: 22, sm: 30 },
                    height: { xs: 22, sm: 30 },
                    borderRadius: '50%',
                    bgcolor: '#dcfce7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Settings color='#21c45d' size={16} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                    Team Management
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, textAlign: 'left',ml: 9 }}>
                  {[
                    "Add team members and control access",
                    "Set permissions and notifications",
                    "Collaborate on code reviews"
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        bgcolor: '#6b7280',
                        flexShrink: 0,
                        mt: 1.5
                      }} />
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Subscription */}
            <Grid item xs={12} md={4}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2, mb: 2 }}>
                  <Box sx={{
                    width: { xs: 22, sm: 30 },
                    height: { xs: 22, sm: 30 },
                    borderRadius: '50%',
                    bgcolor: '#fbbd231a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Lock color='#f59e0b' size={16} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                    Subscription
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, textAlign: 'left',ml: 9 }}>
                  {[
                    "Track monthly PR analysis usage",
                    "Upgrade or downgrade plans",
                    "Manage billing information"
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        bgcolor: '#6b7280',
                        flexShrink: 0,
                        mt: 1.5
                      }} />
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
      </Box>

      {/* Ready to go! Alert Box */}
      <Alert
        severity="success"
        sx={{
          bgcolor: '#dcfce7',
          color: '#000',
          border: 'none',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          '& .MuiAlert-icon': {
            color: '#16a34a'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Ready to go!
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Your AI code review assistant is now active. Create your first pull request to see it in action!
        </Typography>
      </Alert>
    </>
  );
};

export default StartReviewingStep;
